import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface WatchPortalProps {
    children: ReactNode;
    root: string;
}

export default function WatchPortal({ children, root }: WatchPortalProps) {
    const [portalElement, setPortalElement] = useState<HTMLElement | null>(
        () => document.querySelector(root) as HTMLElement
    );
    const currentElementRef = useRef<HTMLElement | null>(portalElement);

    useEffect(() => {
        const checkForPortalElement = () => {
            const element = document.querySelector(root) as HTMLElement;

            if (element !== currentElementRef.current) {
                currentElementRef.current = element;
                setPortalElement(element);
            }
        };

        const observer = new MutationObserver((mutations) => {
            const shouldCheck = mutations.some((mutation) => {
                const target = mutation.target as HTMLElement;
                return (
                    target.matches?.(root) ||
                    target.contains?.(document.querySelector(root))
                );
            });

            if (shouldCheck) {
                checkForPortalElement();
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        return () => observer.disconnect();
    }, [root]);

    if (!portalElement) {
        return null;
    }

    return createPortal(children, portalElement);
}
